import { useState } from 'react';
import { SERVER_URL, UserInfo, RANK_OPTIONS, REMEMBERED_USER_KEY } from '../../api';
import './Auth.css';

interface AuthProps {
  onAuthed: (token: string, user: UserInfo) => void;
}

type Mode = 'login' | 'register';

const PHONE_RE = /^1[3-9]\d{9}$/;

export default function Auth({ onAuthed }: AuthProps) {
  const [mode, setMode] = useState<Mode>('login');
  const [phone, setPhone] = useState(() => {
    try { return localStorage.getItem(REMEMBERED_USER_KEY) || ''; } catch { return ''; }
  });
  const [nickname, setNickname] = useState('');
  const [rank, setRank] = useState('15级');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const switchMode = (m: Mode) => {
    setMode(m);
    setError('');
  };

  const submit = async () => {
    setError('');
    const p = phone.trim();
    if (!PHONE_RE.test(p)) { setError('请输入正确的 11 位手机号'); return; }
    if (mode === 'register' && !nickname.trim()) { setError('请输入昵称'); return; }

    setBusy(true);
    try {
      const path = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const body: Record<string, string> = mode === 'login'
        ? { username: p, password: p }
        : { phone: p, nickname: nickname.trim(), rank };

      const res = await fetch(SERVER_URL + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError(data.error || '操作失败，请重试');
        return;
      }
      // 自动记住手机号：下次打开（手机/电脑）直接自动登录
      try { localStorage.setItem(REMEMBERED_USER_KEY, p); } catch { /* ignore */ }
      onAuthed(data.token, data.user);
    } catch {
      setError('无法连接服务器，请确认后端已启动');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <h2>{mode === 'login' ? '🐣 登录' : '🐣 注册账号'}</h2>
        <p className="auth-sub">
          {mode === 'login' ? '手机号即密码，直接登录' : '一个手机号一个账号 · 棋谱与战绩自动留存'}
        </p>

        <div className="auth-tabs">
          <button className={mode === 'login' ? 'active' : ''} onClick={() => switchMode('login')}>登录</button>
          <button className={mode === 'register' ? 'active' : ''} onClick={() => switchMode('register')}>注册</button>
        </div>

        <div className="auth-field">
          <label>手机号</label>
          <input
            type="tel"
            inputMode="numeric"
            placeholder="11 位手机号"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
            maxLength={11}
            autoComplete="tel"
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
        </div>

        {mode === 'register' && (
          <div className="auth-field">
            <label>昵称</label>
            <input
              type="text"
              placeholder="对局时显示的名字，如「小明」"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              maxLength={20}
            />
          </div>
        )}

        {mode === 'register' && (
          <div className="auth-field">
            <label>等级（你的棋力）</label>
            <select value={rank} onChange={(e) => setRank(e.target.value)} className="auth-select">
              {RANK_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <p className="auth-field-hint">不确定就选 15 级；对弈时还能手动选级别。</p>
          </div>
        )}

        {error && <div className="auth-error">{error}</div>}

        <button className="auth-submit" onClick={submit} disabled={busy || phone.length !== 11}>
          {busy ? '处理中...' : mode === 'login' ? '进入 AI 训练平台' : '注册并登录'}
        </button>

        <p className="auth-hint">登录状态会自动记住，手机和电脑都不用再输密码</p>
      </div>
    </div>
  );
}
